import React, { memo, useState } from 'react';
import Div from '@/shared/Div';
import Container from '@/shared/Container';
import Heading from '@/shared/Heading';
import Paragraph from '@/shared/Paragraph';
import Map from '@/components/Map';
import withContentFlags from '@/hoc/withContentFlags';
import Image from 'next/image';

interface DocumentLink {
  link: string;
  title: string;
}

interface QuarterData {
  title: string;
  links: DocumentLink[];
}

interface YearData {
  [quarterKey: string]: QuarterData;
}

interface FinancialResultsData {
  [year: string]: YearData;
}

export interface FinancialResultsProps {
  title: string;
  data: FinancialResultsData;
}

const FinancialResults = (props: FinancialResultsProps) => {
  const { title, data = {} } = props;

  // Get sorted years in descending order (latest first)
  const years = Object.keys(data).sort((a, b) => {
    const yearA = parseInt(a.split('-')[0]);
    const yearB = parseInt(b.split('-')[0]);
    return yearB - yearA;
  });

  const [activeYear, setActiveYear] = useState(years[0] || '');

  // Get quarters for the active year
  const getQuartersForYear = () => {
    if (!activeYear || !data[activeYear]) return [];

    const yearData = data[activeYear];
    const quarterKeys = Object.keys(yearData);

    // Sort quarters by extracting quarter number (Q1, Q2, Q3, Q4)
    // We want descending order (Q4, Q3, Q2, Q1)
    return quarterKeys
      .sort((a, b) => {
        const numA = parseInt(a.replace('Q', ''));
        const numB = parseInt(b.replace('Q', ''));
        return numB - numA; // Descending order
      })
      .map((quarterKey) => ({
        quarterKey,
        quarterData: yearData[quarterKey],
      }));
  };

  const activeQuarters = getQuartersForYear();

  const renderDocumentLink = (doc: DocumentLink, index: number) => {
    return (
      <Div
        key={index}
        className="pb-4 border-b border-gray-200 group-hover:border-cti-on-primary self-stretch last:border-b-0 last:pb-0 transition-colors"
      >
        <a
          href={doc.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 self-stretch"
        >
          <Paragraph className="text-body-lg text-cm-primary group-hover:text-cti-on-primary font-normal flex-1 transition-colors">
            {doc.title}
          </Paragraph>
          <Image
            src="/images/arrow_forward_ios.svg"
            alt="Arrow forward"
            width={24}
            height={24}
            className="flex-shrink-0 transition-all group-hover:brightness-0 group-hover:saturate-0 group-hover:invert-[81%]"
          />
        </a>
      </Div>
    );
  };

  const renderQuarterCard = (
    quarter: { quarterKey: string; quarterData: QuarterData },
    index: number,
  ) => {
    const { quarterData } = quarter;

    return (
      <Div
        key={index}
        className="group flex flex-col items-start gap-s6 p-6 bg-white hover:bg-cm-brand rounded-2xl border border-gray-200 w-full min-w-[calc(100%-3rem)] md:min-w-0 md:max-w-[290px] transition-colors cursor-pointer"
      >
        {/* Quarter Title */}
        <Heading
          level="3"
          className="text-h-3 text-cti-primary group-hover:text-cti-on-primary transition-colors"
        >
          {quarterData.title}
        </Heading>

        {/* Document Links */}
        <Div className="flex flex-col items-start gap-4 self-stretch">
          <Map list={quarterData.links} render={renderDocumentLink} />
        </Div>
      </Div>
    );
  };

  const renderYearTab = (year: string, index: number) => {
    const isActive = year === activeYear;

    return (
      <button
        key={index}
        onClick={() => setActiveYear(year)}
        className={`px-6 py-3 text-h-4 border-b-2 transition-colors whitespace-nowrap ${
          isActive
            ? 'border-cm-primary text-cti-primary'
            : 'border-transparent text-gray-500 hover:text-cm-primary hover:border-gray-300'
        }`}
      >
        FY {year}
      </button>
    );
  };

  return (
    <Div className="bg-white pt-20 pb-20">
      <Container className="flex flex-col gap-s7">
        <Heading level="1" className="text-hd-1 md:text-desktop-h-1  text-cm-brand">
          {title}
        </Heading>

        {/* Year Tabs */}
        <Div className="flex gap-0 border-b border-gray-200 overflow-x-auto">
          <Map list={years} render={renderYearTab} />
        </Div>

        {/* Quarters Grid */}
        {activeQuarters.length > 0 && (
          <Div className="flex overflow-x-auto gap-s5 md:grid md:grid-cols-2 lg:grid-cols-4">
            <Map list={activeQuarters} render={renderQuarterCard} />
          </Div>
        )}

        {/* Empty State */}
        {activeQuarters.length === 0 && (
          <Div className="text-center py-12">
            <Paragraph className="text-body-lg text-gray-500">
              No financial results available for this year.
            </Paragraph>
          </Div>
        )}
      </Container>
    </Div>
  );
};

export default withContentFlags(memo(FinancialResults));
